import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Card, Col, Input, InputNumber, Row, Tooltip,Popover } from 'antd';
import { useTranslation, withTranslation } from 'react-i18next';
import styled from 'styled-components';
import { debounce } from 'underscore';
import EditorElement from '../../../../components/EditorElement';
import { PAGE_NUMBER } from '../../../../utils/complexDocumentConstants';
import { getPageNoAdditionalContent, pageNumberList } from './pageNumberHelper';
import {
  COMPLEX_DOCUMENT_CONTEXT_KEYS,
  useDocumentContext,
} from '../../../../context/complexDocumentContext';
import { PageLayoutInputLabel } from '../../styles';
import {
  rgb2Hex,
  hexToRgbArr,
  measurementConversion,
  deepCopy,
} from '../../../../utils/javascript';
import ColorPicker from '../../../../artboard/ColorPicker';
import { PAGE_NUMBER_TRANS } from '../../translationValues';
import FontSideBar from '../../FontSideBar';
import { SizeButton, SizeContainer } from '../../../SidebarMenu/styles';
import { ButtonGroup } from 'react-rainbow-components';
import { fontSizes } from '../../TextBlockToolbar';
import { sanitizeFontSize } from '../../../../utils/helper';

const NAMESPACES = ['PAGE_NUMBER_TRANS'];
const DEBOUNCE_TIMEOUT = 300;

const CardContainer = styled.div`
  .ant-card {
    background: black;
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: bold;
    font-size: 14px;
    font-family: system-ui;
    border-radius: 12px;
    padding: 0px;
    .ant-card-body {
      padding: 0px;
      flex-grow: 1;
      text-align: center;
      > div {
        padding: 20px 0px;
        :hover {
          background: hsla(0, 0%, 100%, 0);
        }
      }
    }
  }
`;

const PgNoColorPickerContainer = styled.span`
  height: 16px;
  width: 16px;
  position: relative;
  cursor: pointer;
  margin-top: 10px;
  > div {
    position: absolute;
    z-index: 11;
    margin-top: 20px;
    margin-left: ${({pageNumberType}) => pageNumberType === 1 ? '-22px' : '-148px'};
  }
`;

const PgNoColorPicker = ({ fontColor, onColorChange, pageNumberType }) => {
  const [showPicker, setShowPicker] = useState(false);
  const colorPickerContainerRef = useRef(null);
  useLayoutEffect(() => {
    if (!showPicker || !colorPickerContainerRef.current) return;
    const rect = colorPickerContainerRef.current.getBoundingClientRect();
    const viewportHeight = window.innerHeight;
    if (rect.bottom > viewportHeight) {
      colorPickerContainerRef.current.style.transform = 'translate(0px, -390px)';
    }
  }, [showPicker]);

  return (
    <PgNoColorPickerContainer
      style={{
        backgroundColor: rgb2Hex(fontColor),
      }}
      pageNumberType={pageNumberType}
      onClick={() => setShowPicker(true)}
    >
      {showPicker && (
        <div ref={colorPickerContainerRef} style={{height: '366px'}} >
          <ColorPicker
            onChange={color => onColorChange(color, fontColor)}
            onBlur={() => setShowPicker(false)}
            color={rgb2Hex(fontColor)}
            qlEditor={true}
          />
        </div>
      )}
    </PgNoColorPickerContainer>
  );
};

const PageNumberSettings = ({ flowUUID, sizeIn }) => {
  const { t } = useTranslation(NAMESPACES);
  const [fontListVisible,setFontListVisible] = useState(false);
  const flowSelector = useCallback(store => store[flowUUID], [flowUUID]);
  const [flow, methods] = useDocumentContext(flowSelector, `PageNumberSettings`);
  const pageNumberUUID = flow[PAGE_NUMBER];
  const pageNumberSelector = useCallback(store => store[pageNumberUUID], [pageNumberUUID]);
  const [pageNumber] = useDocumentContext(pageNumberSelector, `PageNumberSettings`);
  const docPageNumberSelector = useCallback(
    state => {
      const styles = state[COMPLEX_DOCUMENT_CONTEXT_KEYS.STYLES];
      if (styles) {
        return styles[PAGE_NUMBER];
      }
      return null;
    },
    [pageNumberUUID],
  );

  const contextSelector = useCallback(store => store[COMPLEX_DOCUMENT_CONTEXT_KEYS.STYLES], []);  
  const [DocumentStyles] = useDocumentContext(contextSelector);
    
  const [docPageNumber] = useDocumentContext(docPageNumberSelector);
  const hiddenRef = useRef();
  const pageNumberType = docPageNumber?.pageNumberType;
  const ofText = docPageNumber?.ofText;
  const contents = docPageNumber?.contents?.contents;
  const style = DocumentStyles?.pageNumber?.contents?.style;
  const isMM = sizeIn === 'mm';
  const inputRef = useRef();  

  useEffect(() => {
    setText(contents?.[0]?.content);
    setLeft(style?.x);
    setTop(style?.y);
  }, [contents, style]);

  const [text, setText] = useState(contents?.[0]?.content);
  const [left, setLeft] = useState(style?.x);
  const [top, setTop] = useState(style?.y);
  
  const currentFontFamily = contents?.[0]?.style?.fontFamily ? contents?.[0]?.style?.fontFamily :  DocumentStyles?.fontFamily ;
  const currentFontSize = contents?.[0]?.style?.fontSize ? contents?.[0]?.style?.fontSize :  DocumentStyles?.fontSize ;

  const [fontFamily, setFontFamily] = useState(currentFontFamily);
  const [defaultStyle, setDefaultStyle] = useState({ fontSize: currentFontSize});

  const debouncedUpdatePageNumber = useCallback(
    debounce(methods.updatePageNumber, DEBOUNCE_TIMEOUT),
    [],
  );

  const updatePageNumberWidthNContent = obj => {
    obj.style = { width: hiddenRef.current.scrollWidth };
    methods.updatePageNumber(obj);
  };

  const debouncedUpdatePgNoWidthNContent = useCallback(
    debounce(updatePageNumberWidthNContent, DEBOUNCE_TIMEOUT),
    [],
  );

  const elements = pageNumberList(t);

  const handleCardClick = ({ pageNumberType: type, contents: defaultContents }) => {
    // return if same page number format is clicked again.
    if (type === pageNumberType) {
      methods.deletePageNumber({ uuid: flowUUID });
      return;
    }
    const pageNumberObj = { pageNumberType: type };
    // if clicked or current page number format is type 1, then copy contents to the store page number
    if (type === 1 || pageNumberType === 1) {
      defaultContents.contents[0].style = contents?.[0].style;
      pageNumberObj.contents = defaultContents.contents;
    }
    methods.updatePageNumber({ uuid: flowUUID, ...pageNumberObj, historyChange: {} });
  };

  const updateLeft = value => {
    const convertedValue = isMM ? measurementConversion(value, 'px') : value;
    setLeft(convertedValue);
    debouncedUpdatePageNumber({ uuid: flowUUID, style: { x: convertedValue }, historyChange: {} });
  };

  const updatetop = value => {
    const convertedValue = isMM ? measurementConversion(value, 'px') : value;
    setTop(convertedValue);
    debouncedUpdatePageNumber({ uuid: flowUUID, style: { y: convertedValue }, historyChange: {} });
  };

  const updatePageText = event => {
    const text = event.target.value;
    const contentCopy = deepCopy(contents);
    contentCopy[0].content = text;
    setText(text);
    debouncedUpdatePgNoWidthNContent({ uuid: flowUUID, contents: contentCopy, historyChange: {} });
  };

  const updateColor = ({ rgb }) => {
    const contentCopy = deepCopy(contents);
    contentCopy[0].style = { ...contentCopy[0].style, color: rgb2Hex([rgb.r, rgb.g, rgb.b]) };
    debouncedUpdatePageNumber({ uuid: flowUUID, contents: contentCopy, historyChange: {} });
  };

  const additionalContent = getPageNoAdditionalContent(pageNumberType, ofText);

  const onFontClick=()=>{
    setFontListVisible(true);
  }

  const updateFontFamily =(newFontFamily)=>{
    setFontFamily(newFontFamily)
    const contentCopy = deepCopy(contents);
    contentCopy[0].style = { ...contentCopy[0].style, fontFamily: newFontFamily };
    debouncedUpdatePageNumber({ uuid: flowUUID, contents: contentCopy, historyChange: {} });
  }

  const closeFontList =()=>{
    setFontListVisible(false);
  }

  const onSizeChange = newFontSize => {
    setDefaultStyle({fontSize: newFontSize})
        const contentCopy = deepCopy(contents);
    contentCopy[0].style = { ...contentCopy[0].style, fontSize: newFontSize };
    debouncedUpdatePageNumber({ uuid: flowUUID, contents: contentCopy, historyChange: {} });
  };

  const handleKeypress = e => {
    e.stopPropagation();
    if (['Enter', 'ArrowDown', 'ArrowUp'].includes(e.key)) {
      e.preventDefault();
      const value = parseFloat(inputRef.current.innerHTML.trim());
      if (!isNaN(value) && value >= 1 && value <= 400) {
        onSizeChange(value);
      }
    }
  };

    const handleKeyDown = e => {
      e.stopPropagation();
  
      const allowedKeys = [
        'Backspace',
        'Delete',
        'ArrowLeft',
        'ArrowRight',
        'Tab',
        'Enter'
      ];
  
      if (allowedKeys.includes(e.key)) return;
  
      if (!/^[0-9.]$/.test(e.key)) {
        e.preventDefault();
        return;
      }
  
      const el = inputRef.current;
      const selection = window.getSelection();
      if (!selection || selection.rangeCount === 0) return;
  
      const range = selection.getRangeAt(0);
      const current = el.innerText || '';
  
      // caret offsets
      const start = range.startOffset;
      const end = range.endOffset;
  
      // 🔑 build next value using caret position
      const next =
        current.slice(0, start) +
        e.key +
        current.slice(end);
  
      const sanitized = sanitizeFontSize(next);
  
      e.preventDefault();
  
      if (!sanitized) return;
  
      el.innerText = sanitized;
  
      // 🔑 restore caret near where user typed
      requestAnimationFrame(() => {
        const newRange = document.createRange();
        const sel = window.getSelection();
  
        const caretPos = Math.min(
          start + 1,
          sanitized.length
        );
  
        newRange.setStart(el.firstChild || el, caretPos);
        newRange.collapse(true);
  
        sel.removeAllRanges();
        sel.addRange(newRange);
      });
    };

     const handlePaste = e => {
        e.preventDefault();
        const pasted = e.clipboardData.getData('text');
        const sanitized = sanitizeFontSize(pasted);
        inputRef.current.innerText = sanitized;
    
        requestAnimationFrame(() => {
          const range = document.createRange();
          const sel = window.getSelection();
          range.selectNodeContents(inputRef.current);
          range.collapse(false);
          sel.removeAllRanges();
          sel.addRange(range);
        });
      };

    const renderSize = () => {
      return (
        <span>
          <ButtonGroup className='fontSize-button' style={{border:'1px solid #d9d9d9', borderRadius:'4px', height: '37px'}}>
            <SizeButton className='fontSize-button' style={{marginLeft: '0px'}}
              comboBox={1}
              isMinus={1}
              onClick={() => {
                if (!isNaN(defaultStyle.fontSize)) {
                  onSizeChange(defaultStyle.fontSize - 1);
                }
              }}
              radiusLeft={true}
              icon="minus"
            />
            <Popover
              content={
                <SizeContainer>
                  <ul>
                    {fontSizes.map(data => (
                      <li
                        key={data}
                        onClick={e => {
                          onSizeChange(parseInt(data?.replaceAll('px', '')) );
                        }}
                      >
                        {data}
                      </li>
                    ))}
                  </ul>
                </SizeContainer>
              }
              title={null}
              trigger="click"
            >
              <div
                ref={inputRef}
                contentEditable={true}
                suppressContentEditableWarning={true}
                className="size-ref ql-size"
                style={{
                  width: '50px',
                  color: '#000',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: '#cdcdcd',
                }}
                onKeyPress={handleKeypress}
                onKeyDown={handleKeyDown}
                onPaste={handlePaste}
                onBlur={e => {
                  const value = parseFloat(inputRef.current.innerHTML.trim());
                  if (value !== defaultStyle.fontSize && !isNaN(value) && value >= 1 && value <= 400) {
                    onSizeChange(value);
                  }
                }}
              >
                {!isNaN(defaultStyle.fontSize) && defaultStyle.fontSize ? defaultStyle.fontSize : ' '}
              </div>
            </Popover>
            <SizeButton className='fontSize-button'
              comboBox={1}
              isMinus={1}
              onClick={() => {
                if (!isNaN(defaultStyle.fontSize)) {
                  onSizeChange(defaultStyle.fontSize + 1);
                }
              }}
              radiusLeft={false}
              icon="plus"
            />
          </ButtonGroup>
        </span>
      );
    };


  return (
    <>
      {pageNumber && docPageNumber && (
        <div className="properties-container">
          <div className="grid">
            <div style={{display:'flex',flexDirection:'row'}}>
            <div className="input-container">
              <PageLayoutInputLabel>
                {t(PAGE_NUMBER_TRANS.LEFT_LABEL.key, PAGE_NUMBER_TRANS.LEFT_LABEL.value)}
              </PageLayoutInputLabel>
              <InputNumber
                type="number"
                size="large"
                value={isMM ? measurementConversion(left, 'mm') : left}
                min={0}
                max={10000}
                onChange={updateLeft}
              />
            </div>
             <div className="input-container">
                <PageLayoutInputLabel>
                  {t(PAGE_NUMBER_TRANS.TOP_LABEL.key, PAGE_NUMBER_TRANS.TOP_LABEL.value)}
                </PageLayoutInputLabel>
                <InputNumber
                  type="number"
                  size="large"
                  min={0}
                  max={10000}
                  value={isMM ? measurementConversion(top, 'mm') : top}
                  onChange={updatetop}
                />
              </div>
            
        </div>
            <div style={{ display: 'flex', flexDirection: 'row' }}>
              <div style={{ margin: '0px 25px 20px 0px' }}>
                <PageLayoutInputLabel>
                  Font Family
                </PageLayoutInputLabel>
                <div class="ql-font" onMouseDown={onFontClick}
                  style={{
                    height: '38px', width: '125px', color: 'black',
                    border: '1px solid #d9d9d9', borderRadius: '4px', display: 'flex', alignItems: 'center', padding: '11px', justifyContent: 'center', cursor: 'pointer'
                  }}>
                  <span style={{ width: '170px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: 'black', fontSize: '14px' }}>
                    {fontFamily}{' '}
                  </span>
                </div>
              </div>  
              <div>
                <PageLayoutInputLabel>
                  Font Size
                </PageLayoutInputLabel>
                {renderSize()}
              </div>
            </div>
          </div>
          <div className="grid">
            {pageNumberType !== 1 && (
              <div className="input-container">
                <PageLayoutInputLabel>
                  {t(PAGE_NUMBER_TRANS.TEXT_LABEL.key, PAGE_NUMBER_TRANS.TEXT_LABEL.value)}
                </PageLayoutInputLabel>
                <Input style={{ width: "125px", height: '38px' }} value={text} onChange={updatePageText} />
              </div>
            )}
            <div className="input-container">
              <PageLayoutInputLabel>
                {t(PAGE_NUMBER_TRANS.COLOR_LABEL.key, PAGE_NUMBER_TRANS.COLOR_LABEL.value)}
              </PageLayoutInputLabel>
              <PgNoColorPicker
                fontColor={hexToRgbArr(contents?.[0]?.style?.color)}
                onColorChange={updateColor}
                pageNumberType={pageNumberType}
              />
            </div>
          </div>
        </div>
      )}
      <div
        style={{
          height: '1px',
          whiteSpace: 'nowrap',
          visibility: 'hidden',
          overflow: 'visible',
          ...contents?.[0]?.style,
        }}
        ref={hiddenRef}
      >
        {text}
        {additionalContent}
      </div>
      <CardContainer>
        <Row gutter={[16, 16]} style={{ margin: '0px' }}>
          {elements?.map(element => {
            const isSelected = element.elementDetails.pageNumberType === pageNumberType;
            return (
              <Col span={12}>
                <Tooltip
                  placement="top"
                  title={t(
                    PAGE_NUMBER_TRANS.DRAG_DROP_INFO.key,
                    PAGE_NUMBER_TRANS.DRAG_DROP_INFO.value,
                  )}
                >
                  <Card
                    bordered={true}
                    style={{
                      border: isSelected ? '1px red solid' : '',
                    }}
                    onClick={() => (!!docPageNumber ? handleCardClick(element.elementDetails) : '')}
                  >
                    <EditorElement
                      element={element}
                      clickElement={() => {}}
                      pageSettings={{}}
                      toggleDragging={() => {}}
                      setIsDragAndDropped={() => {}}
                      draggable={!docPageNumber ? true : false}
                      type={PAGE_NUMBER}
                    />
                  </Card>
                </Tooltip>
              </Col>
            );
          })}
        </Row>
      </CardContainer>
            {fontListVisible &&(
              <FontSideBar
                itemUUID={undefined}
                editor={undefined}
                setFontChange={updateFontFamily}
                fontFamily={fontFamily}
                isToolBarVisible={fontListVisible}
                closeFontList={closeFontList}
                isAbsolute={true}
              />
            )}
    </>
  );
};
export default withTranslation(NAMESPACES)(PageNumberSettings);
